import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardComponent } from 'ng-zorro-antd/card';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { catchError, finalize, of } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type PricingType = 'DurationPackage' | 'PointPackage' | 'AddonPackage';

interface PricingPackageItem {
  id?: string;
  pricingConfigId?: string;
  pricingCode?: string;
  name?: string;
  recommend?: boolean | null;
  basePoints?: number | string | null;
  bonusPoints?: number | string | null;
  displayOrder?: number | string | null;
  durationMonths?: number | string | null;
  addonValue?: number | string | null;
  priceCny?: number | string | null;
  discountPriceCny?: number | string | null;
  discountRate?: number | string | null;
  trialEnabled?: boolean | null;
  trialDays?: number | string | null;
  description?: string;
  deleted?: boolean;
  createTime?: number;
  updateTime?: number;
  deleteTime?: number;
}

interface PricingPackageConfig {
  id?: string;
  pricingCode?: string;
  pricingName?: string;
  pricingType?: PricingType | string | null;
  deleted?: boolean;
  createTime?: number;
  updateTime?: number;
  deleteTime?: number;
  items?: PricingPackageItem[];
}

interface PricingPackageAuditLog {
  id?: string;
  createTime?: number;
  updateTime?: number;
  pricingConfigId?: string;
  pricingItemId?: string;
  pricingCode?: string;
  action?: string;
  beforeData?: string;
  afterData?: string;
  operatorId?: string;
  operatorName?: string;
}

interface PricingPackageAuditChangeRow {
  key: string;
  label: string;
  before: string;
  after: string;
  changed: boolean;
}

interface AuditDrawerContext {
  package?: PricingPackageConfig | null;
  item?: PricingPackageItem | null;
}

interface PricingPackageAuditListPayload {
  pageIndex: number;
  pageSize: number;
  direction: 'asc' | 'desc';
  orderBy: string;
  refreshTime: number;
  pricingConfigId?: string;
  pricingItemId?: string;
  pricingCode?: string;
  key?: string;
}

@Component({
  selector: 'pricing-package-center',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzDrawerModule,
    NzEmptyModule,
    NzIconModule,
    NzInputModule,
    NzInputNumberModule,
    NzModalModule,
    NzPaginationModule,
    NzSpinModule,
    NzSwitchModule,
    NzTableModule,
    NzTagModule,
    NzToolTipModule,
    I18nPipe,
    NzCardComponent,
    TestIdDirective
  ],
  templateUrl: './pricing-package-center.component.html',
  styleUrls: ['./pricing-package-center.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PricingPackageCenterComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  loading = false;
  loadError = false;
  allPackages: PricingPackageConfig[] = [];
  packages: PricingPackageConfig[] = [];
  total = 0;
  refreshTime = Date.now();
  keyword = '';
  readonly loadingPackagePlaceholders = [0, 1, 2];

  packageModalVisible = false;
  packageModalLoading = false;
  editingPackage: PricingPackageConfig | null = null;
  packageDraft: PricingPackageConfig = this.getEmptyPackageDraft();

  itemModalVisible = false;
  itemModalLoading = false;
  editingItem: PricingPackageItem | null = null;
  itemDraft: PricingPackageItem = this.getEmptyItemDraft();
  itemPackage: PricingPackageConfig | null = null;

  auditDrawerVisible = false;
  auditDrawerLoading = false;
  auditLogs: PricingPackageAuditLog[] = [];
  auditPageIndex = 0;
  auditPageSize = 20;
  auditTotalCount = 0;
  auditRefreshTime = 0;
  auditContext: AuditDrawerContext | null = null;
  readonly auditRawDataOpen: Record<string, boolean> = {};
  readonly auditFieldOrder = [
    'name',
    'pricingName',
    'pricingCode',
    'pricingType',
    'description',
    'basePoints',
    'bonusPoints',
    'displayOrder',
    'durationMonths',
    'trialEnabled',
    'trialDays',
    'priceCny',
    'discountPriceCny',
    'discountRate',
    'deleted',
    'createTime',
    'updateTime',
    'deleteTime'
  ];
  readonly auditHiddenFields = new Set(['id', 'pricingConfigId', 'pricingItemId']);
  readonly auditFieldLabels: Record<string, string> = {
    name: 'pricing.package.field.name',
    pricingName: 'pricing.package.audit.field.pricing-name',
    pricingCode: 'pricing.package.audit.field.pricing-code',
    pricingType: 'pricing.package.audit.field.pricing-type',
    description: 'pricing.package.audit.field.description',
    basePoints: 'pricing.package.audit.field.base-points',
    bonusPoints: 'pricing.package.audit.field.bonus-points',
    displayOrder: 'pricing.package.audit.field.display-order',
    durationMonths: 'pricing.package.audit.field.duration-months',
    trialEnabled: 'pricing.package.audit.field.trial-enabled',
    trialDays: 'pricing.package.audit.field.trial-days',
    priceCny: 'pricing.package.audit.field.price',
    discountPriceCny: 'pricing.package.audit.field.discount-price',
    discountRate: 'pricing.package.audit.field.discount-rate',
    deleted: 'pricing.package.audit.field.deleted',
    createTime: 'pricing.package.audit.field.create-time',
    updateTime: 'pricing.package.audit.field.update-time',
    deleteTime: 'pricing.package.audit.field.delete-time'
  };

  t(key: string, params?: Record<string, unknown>): string {
    let text = this.i18n.fanyi(key, params);
    Object.entries(params ?? {}).forEach(([paramKey, value]) => {
      const safeValue = String(value ?? '');
      text = text.replace(new RegExp(`\\{\\{\\s*${paramKey}\\s*\\}\\}`, 'g'), safeValue);
      text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), safeValue);
    });
    return text;
  }

  ngOnInit(): void {
    this.loadPackages();
  }

  loadPackages(): void {
    this.loading = true;
    this.loadError = false;
    this.http
      .post(ConstantUtil.ADMIN_PRICING_PACKAGE_CONFIG_V1_LIST, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          const rawData = res?.data ?? [];
          const rawList = Array.isArray(rawData) ? rawData : (rawData?.items ?? rawData?.list ?? []);
          const normalized = (rawList as PricingPackageConfig[]).map(item => this.normalizePackage(item));
          this.allPackages = normalized;
          this.applyFilters();
        },
        error: () => {
          this.loadError = true;
          this.allPackages = [];
          this.packages = [];
          this.total = 0;
          this.message.error(this.t('common.load-failed'));
        }
      });
  }

  onKeywordChange(keyword: string): void {
    this.keyword = keyword ?? '';
    this.applyFilters();
  }

  search(): void {
    this.keyword = this.keyword.trim();
    this.applyFilters();
  }

  reset(): void {
    this.keyword = '';
    this.applyFilters();
  }

  retryLoad(): void {
    this.loadPackages();
  }

  openPackageModal(pkg?: PricingPackageConfig | null): void {
    this.editingPackage = pkg ? { ...pkg } : null;
    this.packageDraft = this.editingPackage ? { ...this.editingPackage } : this.getEmptyPackageDraft();
    this.packageModalVisible = true;
  }

  closePackageModal(): void {
    this.packageModalVisible = false;
    this.packageModalLoading = false;
    this.editingPackage = null;
    this.packageDraft = this.getEmptyPackageDraft();
  }

  savePackage(): void {
    if (!this.hasRequiredPackageFields(this.packageDraft)) {
      this.message.warning(this.t('pricing.package.validation.required'));
      return;
    }

    const payload = this.buildPackagePayload(this.packageDraft);
    this.packageModalLoading = true;
    this.http
      .post(ConstantUtil.TENANT_PRICING_PACKAGE_CONFIG_V1_SAVE, payload)
      .pipe(
        finalize(() => {
          this.packageModalLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (this.isSuccess(res)) {
            this.message.success(this.t('common.save-success'));
            this.closePackageModal();
            this.loadPackages();
          } else {
            this.message.error(res?.msg || this.t('common.save-failed'));
          }
        },
        error: () => {
          this.message.error(this.t('common.save-failed'));
        }
      });
  }

  confirmDeletePackage(pkg: PricingPackageConfig): void {
    this.modal.confirm({
      nzTitle: this.t('pricing.package.delete.title'),
      nzContent: this.t('pricing.package.delete.content', { name: pkg.pricingName || pkg.pricingCode || '--' }),
      nzOkText: this.t('common.confirm'),
      nzCancelText: this.t('common.cancel'),
      nzOnOk: () => this.deletePackage(pkg)
    });
  }

  deletePackage(pkg: PricingPackageConfig): void {
    const payload = {
      pricingConfigId: pkg.id
    };
    this.http.post(ConstantUtil.TENANT_PRICING_PACKAGE_CONFIG_V1_DELETE, payload).subscribe({
      next: res => {
        if (this.isSuccess(res)) {
          this.message.success(this.t('common.delete-success'));
          this.loadPackages();
        } else {
          this.message.error(res?.msg || this.t('common.delete-failed'));
        }
      },
      error: () => {
        this.message.error(this.t('common.delete-failed'));
      }
    });
  }

  openItemModal(pkg: PricingPackageConfig, item?: PricingPackageItem | null): void {
    this.itemPackage = pkg;
    this.editingItem = item ? { ...item } : null;
    this.itemDraft = this.editingItem ? { ...this.editingItem } : this.getEmptyItemDraft();
    this.itemModalVisible = true;
  }

  openTrialItemModal(pkg: PricingPackageConfig): void {
    this.itemPackage = pkg;
    this.editingItem = null;
    this.itemDraft = {
      ...this.getEmptyItemDraft(),
      trialEnabled: true
    };
    this.itemModalVisible = true;
  }

  closeItemModal(): void {
    this.itemModalVisible = false;
    this.itemModalLoading = false;
    this.editingItem = null;
    this.itemDraft = this.getEmptyItemDraft();
    this.itemPackage = null;
  }

  saveItem(): void {
    if (!this.itemPackage) {
      return;
    }
    if (!this.itemPackage?.id) {
      this.message.warning(this.t('pricing.package.item.validation.required'));
      return;
    }
    if (this.isTrialItem(this.itemDraft)) {
      if (!this.hasRequiredTrialItemFields(this.itemDraft)) {
        this.message.warning(this.t('pricing.package.item.validation.required'));
        return;
      }
      if (this.hasAnotherTrialItem(this.itemPackage, this.itemDraft)) {
        this.message.warning(this.t('pricing.package.item.validation.single-trial'));
        return;
      }
    } else if (this.isPointPackage(this.itemPackage)) {
      if (!this.hasRequiredPointItemFields(this.itemDraft)) {
        this.message.warning(this.t('pricing.package.item.validation.required'));
        return;
      }
    } else if (this.isAddonPackage(this.itemPackage)) {
      if (!this.hasRequiredAddonItemFields(this.itemDraft)) {
        this.message.warning(this.t('pricing.package.item.validation.required'));
        return;
      }
    } else if (!this.hasRequiredStandardItemFields(this.itemDraft)) {
      this.message.warning(this.t('pricing.package.item.validation.required'));
      return;
    }

    const payload = this.buildItemPayload(this.itemPackage, this.itemDraft);
    this.itemModalLoading = true;
    this.http
      .post(ConstantUtil.TENANT_PRICING_PACKAGE_ITEM_V1_SAVE, payload)
      .pipe(
        finalize(() => {
          this.itemModalLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (this.isSuccess(res)) {
            this.message.success(this.t('common.save-success'));
            this.refreshPackageItems(this.itemPackage);
            this.closeItemModal();
          } else {
            this.message.error(res?.msg || this.t('common.save-failed'));
          }
        },
        error: () => {
          this.message.error(this.t('common.save-failed'));
        }
      });
  }

  confirmDeleteItem(pkg: PricingPackageConfig, item: PricingPackageItem): void {
    this.modal.confirm({
      nzTitle: this.t('pricing.package.item.delete.title'),
      nzContent: this.t('pricing.package.item.delete.content', { name: item.description || item.pricingCode || item.id || '--' }),
      nzOkText: this.t('common.confirm'),
      nzCancelText: this.t('common.cancel'),
      nzOnOk: () => this.deleteItem(pkg, item)
    });
  }

  deleteItem(pkg: PricingPackageConfig, item: PricingPackageItem): void {
    const payload = {
      pricingConfigId: pkg.id,
      pricingItemId: item.id
    };
    this.http.post(ConstantUtil.TENANT_PRICING_PACKAGE_ITEM_V1_DELETE, payload).subscribe({
      next: res => {
        if (this.isSuccess(res)) {
          this.message.success(this.t('common.delete-success'));
          this.refreshPackageItems(pkg);
        } else {
          this.message.error(res?.msg || this.t('common.delete-failed'));
        }
      },
      error: () => {
        this.message.error(this.t('common.delete-failed'));
      }
    });
  }

  openAuditDrawer(pkg: PricingPackageConfig, item?: PricingPackageItem | null): void {
    this.auditContext = { package: pkg, item: item ?? null };
    this.auditDrawerVisible = true;
    this.auditPageIndex = 0;
    this.auditPageSize = 20;
    this.auditRefreshTime = 0;
    this.loadAuditLogs();
  }

  closeAuditDrawer(): void {
    this.auditDrawerVisible = false;
    this.auditLogs = [];
    this.auditTotalCount = 0;
    this.auditRefreshTime = 0;
    this.auditPageIndex = 0;
    this.auditPageSize = 20;
    this.auditContext = null;
    Object.keys(this.auditRawDataOpen).forEach(key => delete this.auditRawDataOpen[key]);
  }

  onAuditPageIndexChange(index: number): void {
    if (this.auditDrawerLoading || index - 1 === this.auditPageIndex) {
      return;
    }
    this.auditPageIndex = index - 1;
    this.loadAuditLogs();
  }

  onAuditPageSizeChange(size: number): void {
    if (this.auditDrawerLoading || size === this.auditPageSize) {
      return;
    }
    this.auditPageSize = size;
    this.auditPageIndex = 0;
    this.loadAuditLogs();
  }

  loadAuditLogs(): void {
    if (!this.auditContext?.package) {
      return;
    }
    this.auditDrawerLoading = true;
    const payload: PricingPackageAuditListPayload = {
      pageIndex: this.auditPageIndex,
      pageSize: this.auditPageSize,
      direction: 'desc',
      orderBy: 'id',
      refreshTime: this.auditRefreshTime,
      pricingConfigId: this.auditContext.package.id,
      pricingItemId: this.auditContext.item?.id,
      pricingCode: this.auditContext.package.pricingCode
    };

    this.http
      .post(ConstantUtil.TENANT_PRICING_PACKAGE_AUDIT_V1_LIST, payload)
      .pipe(
        finalize(() => {
          this.auditDrawerLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          const rawData = res?.data ?? [];
          const rawList = Array.isArray(rawData) ? rawData : (rawData?.items ?? rawData?.list ?? []);
          this.auditLogs = rawList as PricingPackageAuditLog[];
          this.auditTotalCount = Number(rawData?.totalCount ?? rawList.length ?? 0);
          this.auditRefreshTime = Number(rawData?.refreshTime ?? this.auditRefreshTime ?? 0);
        },
        error: () => {
          this.auditLogs = [];
          this.auditTotalCount = 0;
          this.message.error(this.t('common.load-failed'));
        }
      });
  }

  trackByPackage(_: number, item: PricingPackageConfig): string {
    return String(item.id ?? item.pricingCode ?? _);
  }

  trackByItem(_: number, item: PricingPackageItem): string {
    return String(item.id ?? item.pricingCode ?? _);
  }

  formatPrice(value?: number | string | null, fractionDigits = 0): string {
    const num = Number(value ?? 0);
    if (!Number.isFinite(num)) return '--';
    return `${this.t('pricing.package.currency.twd')} ${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    }).format(num)}`;
  }

  formatNumber(value?: number | string | null): string {
    const num = Number(value ?? 0);
    if (!Number.isFinite(num)) return '--';
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  }

  formatDiscountRate(value?: number | string | null): string {
    const num = Number(value ?? 0);
    if (!Number.isFinite(num) || num <= 0) return '--';
    const percent = num <= 1 ? num * 100 : num;
    return `${this.formatNumber(percent)}%`;
  }

  getDraftDiscountPriceText(): string {
    const discountPrice = this.calculateDiscountPrice(this.itemDraft.priceCny, this.itemDraft.discountRate);
    return discountPrice === null ? '--' : this.formatNumber(discountPrice);
  }

  formatDate(value?: number | string): string {
    const num = Number(value ?? 0);
    if (!Number.isFinite(num) || num <= 0) return '--';
    const date = new Date(num);
    if (Number.isNaN(date.getTime())) return '--';
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date);
  }

  getAuditActionText(action?: string): string {
    const normalized = String(action ?? '')
      .trim()
      .toUpperCase();
    if (normalized === 'CREATE') return this.t('pricing.package.audit.action.create');
    if (normalized === 'UPDATE') return this.t('pricing.package.audit.action.update');
    if (normalized === 'DELETE') return this.t('pricing.package.audit.action.delete');
    return action || this.t('pricing.package.audit.field-change');
  }

  getAuditSummary(log: PricingPackageAuditLog): string {
    const rows = this.getAuditChangeRows(log);
    const action = String(log.action ?? '')
      .trim()
      .toUpperCase();
    const after = this.parseAuditPayload(log.afterData);
    const before = this.parseAuditPayload(log.beforeData);
    const source = after && Object.keys(after).length ? after : before;
    const name = this.getAuditObjectName(source) || log.pricingCode || '--';

    if (action === 'CREATE') return this.t('pricing.package.audit.summary.create', { name });
    if (action === 'DELETE') return this.t('pricing.package.audit.summary.delete', { name });
    if (rows.length) {
      const fieldNames = rows
        .filter(row => row.changed)
        .slice(0, 3)
        .map(row => row.label)
        .join('、');
      return this.t('pricing.package.audit.summary.update', { fields: fieldNames || this.t('pricing.package.audit.field-change') });
    }
    return this.t('pricing.package.audit.summary.view');
  }

  getAuditChangeRows(log: PricingPackageAuditLog): PricingPackageAuditChangeRow[] {
    const before = this.parseAuditPayload(log.beforeData);
    const after = this.parseAuditPayload(log.afterData);
    const keys = this.getAuditDisplayKeys(before, after);
    const action = String(log.action ?? '')
      .trim()
      .toUpperCase();
    if (!keys.length && (this.getAuditRawFallbackText(log.beforeData) !== '--' || this.getAuditRawFallbackText(log.afterData) !== '--')) {
      return [
        {
          key: 'rawData',
          label: this.t('pricing.package.audit.field.raw-data'),
          before: this.getAuditRawFallbackText(log.beforeData),
          after: this.getAuditRawFallbackText(log.afterData),
          changed: !this.isAuditValueSame(log.beforeData, log.afterData)
        }
      ];
    }

    return keys
      .map(key => {
        const beforeValue = before?.[key];
        const afterValue = after?.[key];
        const changed = !this.isAuditValueSame(beforeValue, afterValue);
        return {
          key,
          label: this.getAuditFieldLabel(key),
          before: this.formatAuditValue(key, beforeValue),
          after: this.formatAuditValue(key, afterValue),
          changed
        };
      })
      .filter(row => action === 'CREATE' || action === 'DELETE' || row.changed || row.before !== '--' || row.after !== '--');
  }

  getAuditRawText(log: PricingPackageAuditLog, type: 'before' | 'after'): string {
    const value = type === 'before' ? log.beforeData : log.afterData;
    const parsed = this.parseAuditPayload(value);
    if (!parsed || !Object.keys(parsed).length) return this.getAuditRawFallbackText(value);
    return JSON.stringify(parsed, null, 2);
  }

  getAuditRawToggleKey(log: PricingPackageAuditLog): string {
    return String(log.id ?? `${log.createTime ?? ''}-${log.action ?? ''}-${log.pricingCode ?? ''}`);
  }

  isAuditRawOpen(log: PricingPackageAuditLog): boolean {
    return !!this.auditRawDataOpen[this.getAuditRawToggleKey(log)];
  }

  toggleAuditRawData(log: PricingPackageAuditLog): void {
    const key = this.getAuditRawToggleKey(log);
    this.auditRawDataOpen[key] = !this.auditRawDataOpen[key];
  }

  get hasKeyword(): boolean {
    return this.keyword.trim().length > 0;
  }

  get isFilteredEmpty(): boolean {
    return !this.loading && !this.loadError && this.hasKeyword && this.allPackages.length > 0 && this.packages.length === 0;
  }

  get totalItemCount(): number {
    return this.allPackages.reduce((sum, pkg) => sum + (pkg.items?.length ?? 0), 0);
  }

  get disabledPackageCount(): number {
    return this.allPackages.filter(pkg => pkg.deleted).length;
  }

  get activePackageCount(): number {
    return this.allPackages.length - this.disabledPackageCount;
  }

  getPackageItemCount(pkg: PricingPackageConfig): number {
    return pkg.items?.length ?? 0;
  }

  isTrialItem(item?: PricingPackageItem | null): boolean {
    return item?.trialEnabled === true;
  }

  hasTrialItem(pkg?: PricingPackageConfig | null): boolean {
    return (pkg?.items ?? []).some(item => this.isTrialItem(item));
  }

  isPointPackage(pkg?: PricingPackageConfig | null): boolean {
    return this.normalizePricingType(pkg?.pricingType) === 'PointPackage';
  }

  isAddonPackage(pkg?: PricingPackageConfig | null): boolean {
    return this.normalizePricingType(pkg?.pricingType) === 'AddonPackage';
  }

  getPricingTypeText(type?: PricingType | string | null): string {
    const pricingType = this.normalizePricingType(type);
    if (pricingType === 'PointPackage') return this.t('pricing.package.pricing-type.point');
    if (pricingType === 'AddonPackage') return this.t('pricing.package.pricing-type.addon');
    return this.t('pricing.package.pricing-type.duration');
  }

  getItemMetricColumnLabel(pkg: PricingPackageConfig): string {
    if (this.isPointPackage(pkg)) return this.t('pricing.package.item.field.total-points');
    if (this.isAddonPackage(pkg)) return this.t('pricing.package.item.field.addon-value');
    return this.t('pricing.package.item.field.duration-months');
  }

  getItemAuxiliaryColumnLabel(pkg: PricingPackageConfig): string {
    return this.t(this.isPointPackage(pkg) ? 'pricing.package.item.field.display-order' : 'pricing.package.item.field.discount');
  }

  getItemPrimaryColumnLabel(pkg: PricingPackageConfig): string {
    return this.t(
      this.isPointPackage(pkg) || this.isAddonPackage(pkg) ? 'pricing.package.field.name' : 'pricing.package.item.field.duration-months'
    );
  }

  getItemSecondaryColumnLabel(pkg: PricingPackageConfig): string {
    return this.t(this.isPointPackage(pkg) ? 'pricing.package.item.field.total-points' : 'pricing.package.item.field.price');
  }

  getItemThirdColumnLabel(pkg: PricingPackageConfig): string {
    return this.t(
      this.isPointPackage(pkg)
        ? 'pricing.package.item.field.price'
        : this.isAddonPackage(pkg)
          ? 'pricing.package.item.field.addon-value'
          : 'pricing.package.item.field.discount'
    );
  }

  getItemNameText(item: PricingPackageItem): string {
    return String(item.name ?? '').trim() || item.description || item.pricingCode || item.id || '--';
  }

  getItemDurationText(pkg: PricingPackageConfig, item: PricingPackageItem): string {
    if (this.isPointPackage(pkg)) {
      const totalPoints = this.getPointTotal(item);
      if (totalPoints === null) return '--';
      const basePoints = this.toNumberOrNull(item.basePoints) ?? 0;
      const bonusPoints = this.toNumberOrNull(item.bonusPoints) ?? 0;
      return bonusPoints > 0
        ? `${this.formatNumber(totalPoints)} (${this.formatNumber(basePoints)} + ${this.formatNumber(bonusPoints)})`
        : this.formatNumber(totalPoints);
    }
    if (this.isAddonPackage(pkg)) {
      const addonValue = this.toNumberOrNull(item.addonValue);
      return addonValue === null ? '--' : this.formatNumber(addonValue);
    }
    if (this.isTrialItem(item)) {
      const trialDays = Number(item.trialDays ?? 0);
      return trialDays > 0 ? `${this.formatNumber(trialDays)} ${this.t('pricing.package.item.duration.days')}` : '--';
    }
    const durationMonths = item.durationMonths ?? '--';
    return `${durationMonths}${durationMonths === '--' ? '' : ` ${this.t('pricing.package.item.duration.months')}`}`;
  }

  getItemPriceText(item: PricingPackageItem): string {
    if (this.isTrialItem(item)) {
      return this.t('pricing.package.item.price.free');
    }
    const discountPrice = this.getResolvedDiscountPrice(item);
    if (discountPrice !== null && discountPrice >= 0) {
      return this.formatPrice(discountPrice);
    }
    return this.formatPrice(item.priceCny);
  }

  getItemDisplayOrderText(item: PricingPackageItem): string {
    const displayOrder = this.toNumberOrNull(item.displayOrder);
    return displayOrder === null ? '--' : this.formatNumber(displayOrder);
  }

  getItemOriginalPriceText(item: PricingPackageItem): string {
    if (this.isTrialItem(item)) {
      return '';
    }
    return this.formatPrice(item.priceCny);
  }

  shouldShowOriginalPrice(item: PricingPackageItem): boolean {
    if (this.isTrialItem(item)) {
      return false;
    }
    const price = this.toNumberOrNull(item.priceCny);
    const discountPrice = this.getResolvedDiscountPrice(item);
    return price !== null && discountPrice !== null && discountPrice >= 0 && discountPrice < price;
  }

  getItemMonthlyAverageText(item: PricingPackageItem): string {
    if (this.isTrialItem(item)) {
      return '--';
    }
    const durationMonths = this.toNumberOrNull(item.durationMonths);
    const discountPrice = this.getResolvedDiscountPrice(item);
    if (!durationMonths || durationMonths <= 0 || discountPrice === null || discountPrice < 0) {
      return '--';
    }
    return this.formatPrice(discountPrice / durationMonths, 2);
  }

  getItemMonthlyAverageLabel(item: PricingPackageItem): string {
    return this.t('pricing.package.item.price.monthly-average', {
      value: this.getItemMonthlyAverageText(item)
    });
  }

  trackByAuditLog(index: number, log: PricingPackageAuditLog): string {
    return String(log.id ?? `${log.createTime ?? ''}-${index}`);
  }

  trackByAuditChange(_: number, row: PricingPackageAuditChangeRow): string {
    return row.key;
  }

  private getEmptyPackageDraft(): PricingPackageConfig {
    return {
      pricingCode: '',
      pricingName: '',
      pricingType: 'DurationPackage'
    };
  }

  private getEmptyItemDraft(): PricingPackageItem {
    return {
      basePoints: null,
      bonusPoints: null,
      displayOrder: null,
      durationMonths: null,
      name: '',
      recommend: false,
      priceCny: null,
      discountPriceCny: null,
      discountRate: null,
      trialEnabled: false,
      trialDays: null,
      addonValue: null,
      description: ''
    };
  }

  private normalizePackage(pkg: PricingPackageConfig): PricingPackageConfig {
    const pricingType = this.normalizePricingType(pkg.pricingType);
    const items = Array.isArray(pkg.items) ? pkg.items : [];
    const normalizedItems = (items as PricingPackageItem[]).map(item => this.normalizeItem(item));
    return {
      ...pkg,
      pricingType,
      items: pricingType === 'PointPackage' ? this.sortPointPackageItems(normalizedItems) : normalizedItems
    };
  }

  private normalizeItem(item: PricingPackageItem): PricingPackageItem {
    return {
      ...item,
      name: String(item.name ?? '').trim(),
      recommend: item.recommend === true,
      basePoints: this.toNumberOrNull(item.basePoints),
      bonusPoints: this.toNumberOrNull(item.bonusPoints),
      displayOrder: this.toNumberOrNull(item.displayOrder),
      trialEnabled: item.trialEnabled === true,
      trialDays: this.toNumberOrNull(item.trialDays),
      durationMonths: this.toNumberOrNull(item.durationMonths),
      addonValue: this.toNumberOrNull(item.addonValue),
      priceCny: this.toNumberOrNull(item.priceCny),
      discountPriceCny: this.toNumberOrNull(item.discountPriceCny),
      discountRate: this.toNumberOrNull(item.discountRate)
    };
  }

  private buildPackagePayload(draft: PricingPackageConfig): Record<string, unknown> {
    return {
      id: draft.id,
      pricingCode: String(draft.pricingCode ?? '').trim(),
      pricingName: String(draft.pricingName ?? '').trim(),
      pricingType: this.normalizePricingType(draft.pricingType)
    };
  }

  private buildItemPayload(pkg: PricingPackageConfig, draft: PricingPackageItem): Record<string, unknown> {
    if (this.isPointPackage(pkg)) {
      return {
        id: draft.id,
        pricingConfigId: pkg.id,
        name: String(draft.name ?? '').trim(),
        basePoints: this.toNumberOrNull(draft.basePoints) ?? 0,
        bonusPoints: this.toNumberOrNull(draft.bonusPoints) ?? 0,
        priceCny: this.toNumberOrNull(draft.priceCny),
        description: String(draft.description ?? '').trim()
      };
    }
    if (this.isAddonPackage(pkg)) {
      return {
        id: draft.id,
        pricingConfigId: pkg.id,
        name: String(draft.name ?? '').trim(),
        addonValue: this.toNumberOrNull(draft.addonValue),
        priceCny: this.toNumberOrNull(draft.priceCny),
        description: String(draft.description ?? '').trim()
      };
    }
    if (this.isTrialItem(draft)) {
      return {
        id: draft.id,
        pricingConfigId: pkg.id,
        durationMonths: 1,
        priceCny: 0,
        discountPriceCny: 0,
        discountRate: 0,
        trialEnabled: true,
        trialDays: this.toNumberOrNull(draft.trialDays) ?? 0,
        description: String(draft.description ?? '').trim()
      };
    }
    const priceCny = this.toNumberOrNull(draft.priceCny);
    const discountRate = this.toNumberOrNull(draft.discountRate) ?? 0;
    const discountPriceCny = this.calculateDiscountPrice(priceCny, discountRate) ?? priceCny ?? 0;

    return {
      id: draft.id,
      pricingConfigId: pkg.id,
      durationMonths: this.toNumberOrNull(draft.durationMonths),
      priceCny,
      discountPriceCny,
      discountRate,
      trialEnabled: false,
      trialDays: 0,
      description: String(draft.description ?? '').trim()
    };
  }

  private getResolvedDiscountPrice(item: PricingPackageItem): number | null {
    const discountPrice = this.toNumberOrNull(item.discountPriceCny);
    if (discountPrice !== null && discountPrice >= 0) {
      return discountPrice;
    }
    return this.calculateDiscountPrice(item.priceCny, item.discountRate);
  }

  private calculateDiscountPrice(
    priceValue: number | string | null | undefined,
    discountRateValue: number | string | null | undefined
  ): number | null {
    const price = this.toNumberOrNull(priceValue);
    if (price === null || price < 0) {
      return null;
    }

    const discountRate = this.toNumberOrNull(discountRateValue);
    if (discountRate === null || discountRate <= 0) {
      return price;
    }

    const normalizedRate = discountRate <= 1 ? discountRate : discountRate / 100;
    const safeRate = Math.min(Math.max(normalizedRate, 0), 1);
    return Number((price * (1 - safeRate)).toFixed(0));
  }

  private parseAuditPayload(value: unknown): Record<string, unknown> {
    if (!value) return {};
    if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
    if (typeof value !== 'string') return {};
    const text = value.trim();
    if (!text || text === '--') return {};
    try {
      const parsed = JSON.parse(text);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }

  private getAuditRawFallbackText(value: unknown): string {
    if (value === null || value === undefined || value === '') return '--';
    if (typeof value === 'string') return value.trim() || '--';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  }

  private getAuditDisplayKeys(before: Record<string, unknown>, after: Record<string, unknown>): string[] {
    const keySet = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
    const visibleKeys = [...keySet].filter(key => !this.auditHiddenFields.has(key));
    const orderedKeys = this.auditFieldOrder.filter(key => visibleKeys.includes(key));
    const extraKeys = visibleKeys.filter(key => !this.auditFieldOrder.includes(key)).sort((a, b) => a.localeCompare(b));
    return [...orderedKeys, ...extraKeys];
  }

  private getAuditFieldLabel(key: string): string {
    const labelKey = this.auditFieldLabels[key];
    return labelKey ? this.t(labelKey) : key;
  }

  private formatAuditValue(key: string, value: unknown): string {
    if (value === null || value === undefined || value === '') return '--';
    if (key === 'pricingType') return this.getPricingTypeText(value as PricingType | string | null);
    if (key === 'priceCny' || key === 'discountPriceCny') return this.formatPrice(value as number | string | null);
    if (key === 'discountRate') return this.formatDiscountRate(value as number | string | null);
    if (key === 'basePoints' || key === 'bonusPoints' || key === 'displayOrder') return this.formatNumber(value as number | string | null);
    if (key === 'durationMonths')
      return `${this.formatNumber(value as number | string | null)} ${this.t('pricing.package.item.duration.months')}`;
    if (key === 'trialDays') return `${this.formatNumber(value as number | string | null)} ${this.t('pricing.package.item.duration.days')}`;
    if (key === 'createTime' || key === 'updateTime' || key === 'deleteTime') return this.formatDate(value as number | string);
    if (typeof value === 'boolean') return value ? this.t('pricing.package.audit.value.yes') : this.t('pricing.package.audit.value.no');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  private isAuditValueSame(before: unknown, after: unknown): boolean {
    return this.formatComparableAuditValue(before) === this.formatComparableAuditValue(after);
  }

  private formatComparableAuditValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  private getAuditObjectName(value: Record<string, unknown>): string {
    return String(value?.['name'] ?? value?.['description'] ?? value?.['pricingName'] ?? value?.['pricingCode'] ?? '').trim();
  }

  private toNumberOrNull(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  private hasRequiredPackageFields(draft: PricingPackageConfig): boolean {
    return (
      !!String(draft.pricingCode ?? '').trim() && !!String(draft.pricingName ?? '').trim() && !!this.normalizePricingType(draft.pricingType)
    );
  }

  private hasRequiredTrialItemFields(draft: PricingPackageItem): boolean {
    const trialDays = this.toNumberOrNull(draft.trialDays);
    return !!trialDays && trialDays >= 1 && !!String(draft.description ?? '').trim();
  }

  private hasRequiredStandardItemFields(draft: PricingPackageItem): boolean {
    const durationMonths = this.toNumberOrNull(draft.durationMonths);
    const priceCny = this.toNumberOrNull(draft.priceCny);
    return !!durationMonths && durationMonths >= 1 && priceCny !== null && priceCny >= 0 && !!String(draft.description ?? '').trim();
  }

  private hasRequiredPointItemFields(draft: PricingPackageItem): boolean {
    const name = String(draft.name ?? '').trim();
    const basePoints = this.toNumberOrNull(draft.basePoints);
    const bonusPoints = this.toNumberOrNull(draft.bonusPoints);
    const priceCny = this.toNumberOrNull(draft.priceCny);
    return (
      !!name &&
      basePoints !== null &&
      basePoints > 0 &&
      bonusPoints !== null &&
      bonusPoints >= 0 &&
      priceCny !== null &&
      priceCny >= 0 &&
      !!String(draft.description ?? '').trim()
    );
  }

  private hasRequiredAddonItemFields(draft: PricingPackageItem): boolean {
    const name = String(draft.name ?? '').trim();
    const addonValue = this.toNumberOrNull(draft.addonValue);
    const priceCny = this.toNumberOrNull(draft.priceCny);
    return (
      !!name && addonValue !== null && addonValue > 0 && priceCny !== null && priceCny >= 0 && !!String(draft.description ?? '').trim()
    );
  }

  private applyKeywordFilter(list: PricingPackageConfig[], keyword: string): PricingPackageConfig[] {
    const text = String(keyword ?? '')
      .trim()
      .toLowerCase();
    if (!text) return list;
    return list.filter(item => {
      const code = String(item.pricingCode ?? '').toLowerCase();
      const name = String(item.pricingName ?? '').toLowerCase();
      return code.includes(text) || name.includes(text);
    });
  }

  private applyFilters(): void {
    this.packages = this.applyKeywordFilter(this.allPackages, this.keyword);
    this.total = this.packages.length;
    this.refreshTime = Date.now();
  }

  private refreshPackageItems(pkg?: PricingPackageConfig | null): void {
    const packageId = String(pkg?.id ?? '').trim();
    if (!packageId) {
      this.loadPackages();
      return;
    }

    this.http
      .post(ConstantUtil.TENANT_PRICING_PACKAGE_ITEM_V1_LIST, { pricingConfigId: packageId })
      .pipe(catchError(() => of({ success: false, data: pkg?.items ?? [] })))
      .subscribe({
        next: res => {
          const isSuccess = this.isSuccess(res);
          const rawItems = isSuccess ? res?.data : pkg?.items;
          const items = Array.isArray(rawItems) ? rawItems : [];
          this.allPackages = this.allPackages.map(item =>
            String(item.id ?? '').trim() === packageId ? this.normalizePackage({ ...item, items }) : item
          );
          this.applyFilters();
          this.cdr.detectChanges();
        },
        error: () => {
          this.loadPackages();
        }
      });
  }

  private hasAnotherTrialItem(pkg: PricingPackageConfig, draft: PricingPackageItem): boolean {
    const editingId = String(draft.id ?? '').trim();
    return (pkg.items ?? []).some(item => {
      if (!this.isTrialItem(item)) return false;
      const itemId = String(item.id ?? '').trim();
      return !editingId || itemId !== editingId;
    });
  }

  private isSuccess(res: any): boolean {
    return !!res?.success || res?.status === 0 || res?.code === 0;
  }

  private normalizePricingType(type?: PricingType | string | null): PricingType {
    if (type === 'PointPackage') return 'PointPackage';
    if (type === 'AddonPackage') return 'AddonPackage';
    return 'DurationPackage';
  }

  private getPointTotal(item: PricingPackageItem): number | null {
    const basePoints = this.toNumberOrNull(item.basePoints);
    const bonusPoints = this.toNumberOrNull(item.bonusPoints) ?? 0;
    if (basePoints === null) return null;
    return basePoints + bonusPoints;
  }

  private sortPointPackageItems(items: PricingPackageItem[]): PricingPackageItem[] {
    return [...items].sort((left, right) => {
      const leftOrder = this.toNumberOrNull(left.displayOrder) ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = this.toNumberOrNull(right.displayOrder) ?? Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return String(left.id ?? '').localeCompare(String(right.id ?? ''));
    });
  }
}
